import { useState } from "react";
import { BiCheck, BiEdit } from "react-icons/bi";
import { CgPhone } from "react-icons/cg";
import { IoWarning } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { updateChannelId, updateChannelIdError } from "../../redux/actions/InfoActions";
import { formatPhoneNumberUsingFormats } from "../../utils/utilPhoneNumbers";
import { checkChannelStatus } from "../../api/setup";

const ChannelSection = ({ canEditChannel }) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [showConflictTooltip, setShowConflictTooltip] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const { updatedChannelId, channelIdError, channelId, token, organizationId, accountId, setupId } =
    useSelector((state) => state.info);

  const handleChannelIdChange = (e) => {
    dispatch(updateChannelId(e.target.value));
  };

  const validateChannelId = async () => {
    setIsEditing(false);

    if (!updatedChannelId || updatedChannelId === channelId) {
      dispatch(updateChannelIdError(null));
      return;
    }

    setIsChecking(true);
    try {
      const response = await checkChannelStatus(updatedChannelId, token);
      if (response && response.code === "channel_id_already_in_use") {
        const { scope, conflictInfo } = response;
        if (
          scope.organizationId !== organizationId ||
          scope.accountId !== accountId ||
          scope.setupId !== setupId
        ) {
          dispatch(updateChannelIdError({ conflict: { scope, conflictInfo } }));
        } else {
          dispatch(updateChannelIdError(null));
        }
      } else {
        dispatch(updateChannelIdError(null));
      }
    } catch (error) {
      console.error("Error checking channel status:", error);
    } finally {
      setIsChecking(false);
    }
  };

  const hasConflict = channelIdError?.conflict;

  return (
    <div id="channel-id" className="flex flex-col gap-2">
      <div
        className={`flex flex-row items-center justify-center gap-2 p-2 rounded-md ${
          channelIdError ? "bg-red-100" : ""
        }`}
      >
        <CgPhone className="text-slate-800 w-4 h-4 md:w-5 md:h-5" />
        {isEditing ? (
          <input
            title="Channel ID"
            placeholder="Enter Channel ID"
            value={updatedChannelId || ""}
            onChange={handleChannelIdChange}
            onBlur={validateChannelId}
            onSubmit={validateChannelId}
            autoFocus
            className="text-slate-800 text-xs md:text-[16px] font-semibold hover:cursor-text max-w-[120px] md:max-w-[150px]"
          />
        ) : (
          <span
            className={
              "text-slate-800 text-xs md:text-[16px] font-semibold " +
              (canEditChannel ? "cursor-text" : " cursor-default")
            }
            onClick={() => setIsEditing(canEditChannel)}
          >
            {formatPhoneNumberUsingFormats(updatedChannelId || "Set channel ID")}
          </span>
        )}
        {canEditChannel && (
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="text-slate-600 hover:text-slate-800"
          >
            {isEditing ? (
              <BiCheck className="w-4 h-4 md:w-5 md:h-5" />
            ) : (
              <BiEdit className="w-4 h-4 md:w-5 md:h-5" />
            )}
          </button>
        )}
        {hasConflict && (
          <div className="relative">
            <IoWarning
              className="text-red-500 w-4 h-4 md:w-5 md:h-5 cursor-pointer"
              onMouseEnter={() => setShowConflictTooltip(true)}
              onMouseLeave={() => setShowConflictTooltip(false)}
              onClick={() => setShowConflictTooltip(!showConflictTooltip)}
            />
            {showConflictTooltip && (
              <div className="absolute right-0 top-6 z-50 bg-white border border-red-200 rounded-lg shadow-lg p-3 min-w-[250px] max-w-[320px]">
                <div className="text-red-600 font-semibold text-sm mb-2">Channel ID Conflict</div>
                <div className="text-xs text-slate-600">
                  <div>This channel is already in use by:</div>
                  <div className="bg-slate-50 p-2 rounded mt-1">
                    <span className="font-medium text-slate-800">
                      {hasConflict.conflictInfo?.accountName ||
                        hasConflict.scope?.accountId ||
                        "N/A"}
                      {" / "}
                      {hasConflict.conflictInfo?.setupName || hasConflict.scope?.setupId || "N/A"}
                      {hasConflict.conflictInfo?.agentName &&
                        ` / ${hasConflict.conflictInfo.agentName}`}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelSection;
